// Fixed-menu booking mode — rendered when the customer's booking window
// overlaps an active special_date_event (Mother's Day, NYE…). Guests pick
// a party size from a fixed list, see the set menu, and pay in full.
//
// Optional seat selection: if the event is pinned to a floor_plan_id, the
// regular seat picker renders below the menu, constrained to that plan.

import type {
  WidgetSpecialDateEvent,
  WidgetSpecialDateMenu,
} from "../types";
import { optimizedImageUrl } from "../lib/image";
import {
  guestMenusOf,
  specialDateAddBlocker,
  specialDateTotalCents,
  splitTotal,
  stepSplit,
  type GuestMenuSplit,
} from "../state/special-date";

export function FixedMenuBanner({
  event,
}: {
  event: WidgetSpecialDateEvent;
}) {
  return (
    <div className="vt-fixed-banner">
      {event.hero_image_url && (
        <img
          src={optimizedImageUrl(event.hero_image_url, { width: 192 })}
          alt={event.name}
          loading="lazy"
          decoding="async"
        />
      )}
      <div className="vt-fixed-banner-body">
        <h3>{event.name}</h3>
        {event.banner_copy && <p>{event.banner_copy}</p>}
        <div className="vt-fixed-banner-meta">
          {formatWindow(event.starts_at, event.ends_at)}
        </div>
      </div>
    </div>
  );
}

export function FixedMenuSummary({
  menu,
}: {
  menu: WidgetSpecialDateMenu;
}) {
  const hasCourses = (menu.courses ?? []).length > 0;
  const hasInclusions = (menu.inclusions ?? []).length > 0;
  if (!hasCourses && !hasInclusions) return null;

  return (
    <div className="vt-fixed-menu">
      {hasInclusions && (
        <ul className="vt-fixed-inclusions">
          {(menu.inclusions ?? []).map((i) => (
            <li key={i}>{i}</li>
          ))}
        </ul>
      )}
      {hasCourses &&
        (menu.courses ?? []).map((c) => (
          <div key={c.name} className="vt-fixed-course">
            <div className="vt-fixed-course-name">{c.name}</div>
            <ul>
              {c.options.map((o) => (
                <li key={o}>{o}</li>
              ))}
            </ul>
          </div>
        ))}
    </div>
  );
}

export function FixedPartyPicker({
  event,
  value,
  onChange,
  hideTotal = false,
}: {
  event: WidgetSpecialDateEvent;
  value: number;
  onChange: (n: number) => void;
  /** The menu split shows the total instead (a date with a menu choice). */
  hideTotal?: boolean;
}) {
  const options =
    event.party_size_options.length > 0
      ? event.party_size_options
      : [2, 4, 6, 8];
  const price = (event.price_per_guest_cents / 100) * value;
  const variable = event.guest_selection_mode === "variable";
  const lo = Math.max(1, event.party_size_min ?? 1);
  const hi = Math.max(lo, event.party_size_max ?? event.party_size_min ?? 20);
  return (
    <div className="vt-fixed-party">
      <div className="vt-fixed-party-label">
        {variable ? "Number of guests" : "Party size"}
      </div>
      {variable ? (
        <div className="vt-fixed-party-stepper">
          <button
            type="button"
            className="vt-fixed-party-step"
            aria-label="One fewer guest"
            disabled={value <= lo}
            onClick={() => onChange(Math.max(lo, value - 1))}
          >
            −
          </button>
          <span className="vt-fixed-party-count">{value}</span>
          <button
            type="button"
            className="vt-fixed-party-step"
            aria-label="One more guest"
            disabled={value >= hi}
            onClick={() => onChange(Math.min(hi, value + 1))}
          >
            +
          </button>
          <span className="vt-fixed-party-range">
            {lo}–{hi} guests
          </span>
        </div>
      ) : (
        <div className="vt-fixed-party-chips" role="radiogroup">
          {options.map((n) => {
            const active = n === value;
            return (
              <button
                key={n}
                type="button"
                role="radio"
                aria-checked={active}
                className={`vt-fixed-party-chip${active ? " vt-fixed-party-chip-active" : ""}`}
                onClick={() => onChange(n)}
              >
                {n}
              </button>
            );
          })}
        </div>
      )}
      {!hideTotal && (
        <div className="vt-fixed-party-total">
          Total · {event.currency} {price.toLocaleString()}
        </div>
      )}
    </div>
  );
}

// The menu choice (20260925090010): a date with two or more guest menus asks
// how many guests take each. Fixed party sizes: the guest picks the party
// chip first, then splits it — a step never pushes the split past the party.
// Variable range: the steppers ARE the count; the party follows the split.
export function GuestMenuSplitPicker({
  event,
  partySize,
  split,
  onChange,
}: {
  event: WidgetSpecialDateEvent;
  partySize: number;
  split: GuestMenuSplit;
  onChange: (next: GuestMenuSplit) => void;
}) {
  const menus = guestMenusOf(event);
  const variable = event.guest_selection_mode === "variable";
  const hi = variable ? event.party_size_max ?? null : partySize;
  const total = splitTotal(split);
  const blocker = specialDateAddBlocker(event, partySize, split);
  const cents = specialDateTotalCents(event, partySize, split);
  return (
    <div className="vt-fixed-party vt-menu-split">
      <div className="vt-fixed-party-label">Choose a menu for each guest</div>
      <div className="vt-menu-split-rows" role="group" aria-label="Menus">
        {menus.map((m) => {
          const n = split[m.id] ?? 0;
          const atCap = hi != null && total >= hi;
          return (
            <div key={m.id} className="vt-menu-split-row">
              <div className="vt-menu-split-body">
                <div className="vt-menu-split-name">{m.name}</div>
                {m.description && (
                  <div className="vt-menu-split-desc">{m.description}</div>
                )}
                <div className="vt-menu-split-price">
                  {event.currency} {(m.price_per_guest_cents / 100).toLocaleString()} per guest
                </div>
              </div>
              <div className="vt-fixed-party-stepper">
                <button
                  type="button"
                  className="vt-fixed-party-step"
                  aria-label={`One fewer ${m.name}`}
                  disabled={n <= 0}
                  onClick={() => onChange(stepSplit(split, m.id, -1, hi))}
                >
                  −
                </button>
                <span className="vt-fixed-party-count" aria-live="polite">{n}</span>
                <button
                  type="button"
                  className="vt-fixed-party-step"
                  aria-label={`One more ${m.name}`}
                  disabled={atCap}
                  onClick={() => onChange(stepSplit(split, m.id, +1, hi))}
                >
                  +
                </button>
              </div>
            </div>
          );
        })}
      </div>
      <div className="vt-fixed-party-total">
        {blocker ? (
          <span className="vt-menu-split-hint">{blocker}</span>
        ) : (
          <>
            Total · {event.currency} {(cents / 100).toLocaleString()} · {total} guest{total === 1 ? "" : "s"}
          </>
        )}
      </div>
    </div>
  );
}

function formatWindow(starts: string, ends: string): string {
  const a = new Date(starts);
  const b = new Date(ends);
  const sameDay = a.toDateString() === b.toDateString();
  const day = a.toLocaleDateString("en-ZA", {
    weekday: "long",
    day: "numeric",
    month: "long",
  });
  const t1 = a.toLocaleTimeString("en-ZA", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
  const t2 = b.toLocaleTimeString("en-ZA", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
  if (!sameDay) {
    const day2 = b.toLocaleDateString("en-ZA", {
      weekday: "long",
      day: "numeric",
      month: "long",
    });
    return `${day} ${t1} → ${day2} ${t2}`;
  }
  return `${day} · ${t1}–${t2}`;
}

// Shared helper used by ServicePanel to pick the matching event (if any)
// for the current booking window.
export function matchSpecialDateEvent(
  events: WidgetSpecialDateEvent[],
  starts: string,
  ends: string,
): WidgetSpecialDateEvent | null {
  const a = new Date(starts).getTime();
  const b = new Date(ends).getTime();
  return (
    events
      .filter((e) => e.ends_at && e.starts_at)
      .find((e) => {
        const es = new Date(e.starts_at).getTime();
        const ee = new Date(e.ends_at).getTime();
        return a < ee && b > es; // half-open overlap
      }) ?? null
  );
}
